import React from "react";
import type { ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: (error: Error | null) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// eslint-disable-next-line react-refresh/only-export-components -- fallback component stays colocated with the class boundary to preserve existing module shape.
function ErrorFallback() {
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-stone-50 px-6 py-12">
      <div className="max-w-xl w-full bg-white rounded-xl border border-stone-200 shadow-sm p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-red-600 text-lg" />
          </div>
          <h1 className="text-xl font-semibold text-stone-800">We couldn’t load this page</h1>
        </div>

        <p className="text-sm text-stone-500">An unexpected problem occurred. Please reload the page or return to the portal home page.</p>

        <div className="mt-8 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => {
              if (typeof window !== "undefined") {
                window.location.reload();
              }
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-blue text-white text-sm font-medium hover:bg-brand-blue transition-colors"
          >
            <i className="ri-refresh-line" />
            Reload page
          </button>
          <button
            type="button"
            onClick={() => {
              if (typeof window !== "undefined") {
                window.location.href = "/";
              }
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-stone-200 text-stone-700 text-sm font-medium hover:bg-stone-50 transition-colors"
          >
            <i className="ri-home-line" />
            Go to home
          </button>
        </div>
      </div>
    </div>
  );
}

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("[Portal ErrorBoundary] Caught error:", error, errorInfo);
  }

  override render() {
    if (this.state.hasError) {
      return this.props.fallback ? this.props.fallback(this.state.error) : <ErrorFallback />;
    }
    return this.props.children;
  }
}
